import { useEffect, useRef, useState, type FormEvent } from 'react';
import { trackPageView, Analytics } from '../utils/analytics';
import { sendContactForm, type ContactFormData } from '../api/contact';
import '../styles/audit.css';
import '../styles/reports.css';

/* ============================================
   INLINE ICON COMPONENTS
   ============================================ */

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconArrow = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5l7 7-7 7" />
  </svg>
);

const IconMail = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="22 6 12 13 2 6" />
  </svg>
);

const IconShield = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

/* ============================================
   REFS CAROUSEL — auto-discovery
   ============================================ */

// Vite scans src/assets/audit/logos/ at build time and returns { path: url }
const logoModules = import.meta.glob('../assets/audit/logos/client-*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

// Sort by numeric suffix so client-10 comes after client-9
const logos: string[] = Object.entries(logoModules)
  .map(([path, url]) => {
    const m = path.match(/client-(\d+)\.png$/);
    return { n: m ? parseInt(m[1], 10) : 0, url };
  })
  .sort((a, b) => a.n - b.n)
  .map(x => x.url);

// Speed of auto-scroll in pixels per second
const MARQUEE_SPEED = 40;

function RefsCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isHovered = useRef(false);
  const isDragRef = useRef(false);
  const dragState = useRef<{ startX: number; scrollLeft: number; moved: boolean } | null>(null);

  // We render the logos 3× to create an infinite loop illusion.
  // scrollLeft moves from within the "middle copy" — when it crosses into the third,
  // we teleport back by one-copy-width (invisible to the eye because content is identical).
  const COPIES = 3;
  const displayLogos = Array.from({ length: COPIES }, () => logos).flat();

  useEffect(() => {
    const el = trackRef.current;
    if (!el || logos.length === 0) return;

    // Wait for layout, then position scroll to middle copy
    const initScroll = () => {
      const oneCopyWidth = el.scrollWidth / COPIES;
      el.scrollLeft = oneCopyWidth;
    };
    initScroll();
    // Re-init on resize (tile widths change)
    const onResize = () => initScroll();
    window.addEventListener('resize', onResize);

    let rafId = 0;
    let lastTime = performance.now();

    const loop = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!isHovered.current && !isDragRef.current) {
        el.scrollLeft += MARQUEE_SPEED * dt;
      }

      // Infinite wrap
      const oneCopyWidth = el.scrollWidth / COPIES;
      if (el.scrollLeft >= oneCopyWidth * 2) {
        el.scrollLeft -= oneCopyWidth;
      } else if (el.scrollLeft < oneCopyWidth * 0.5 && dragState.current === null) {
        // User manually scrolled back past middle — wrap to near end
        el.scrollLeft += oneCopyWidth;
      }

      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { startX: e.clientX, scrollLeft: el.scrollLeft, moved: false };
    isDragRef.current = true;
    setIsDragging(true);
    el.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el || !dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 4) dragState.current.moved = true;
    el.scrollLeft = dragState.current.scrollLeft - dx;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (el) el.releasePointerCapture?.(e.pointerId);
    isDragRef.current = false;
    setIsDragging(false);
    setTimeout(() => { dragState.current = null; }, 50);
  };

  if (logos.length === 0) return null;

  return (
    <div className="audit-refs-carousel">
      <div
        ref={trackRef}
        className={`audit-refs-track${isDragging ? ' audit-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={() => { isHovered.current = true; }}
        onMouseLeave={() => { isHovered.current = false; }}
      >
        {displayLogos.map((url, i) => (
          <div key={i} className="audit-ref-tile">
            <img src={url} alt="Reference klienta" loading="lazy" draggable={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================
   NAV
   ============================================ */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav className={`audit-nav${scrolled ? ' scrolled' : ''}`}>
      <div className="audit-nav-inner">
        <a href="/" className="audit-nav-logo">
          <img src="/logo_light2.png" alt="SyncroX" />
        </a>
        <a href="#form" className="audit-nav-cta">Chci audit zdarma</a>
      </div>
    </nav>
  );
}

/* ============================================
   FORM
   ============================================ */

function AuditForm() {
  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: 'digitalizacni-audit-evergreen', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Mám zájem o digitalizační audit.',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Hotovo. Ozveme se do 24 hodin a domluvíme termín.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: 'digitalizacni-audit-evergreen', message: '' });
        Analytics.contactFormSubmit('digitalizacni-audit-evergreen');
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo. Zkuste to prosím znovu.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Chyba připojení. Napište nám na obchod@syncrox.cz.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="audit-form" onSubmit={handleSubmit}>
      <h3>Chci digitalizační audit</h3>
      <div className="audit-form-sub">60 min. Zdarma. Bez závazku.</div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>Jméno *</label>
          <input className="audit-form-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jan Novák" />
        </div>
        <div className="audit-form-group">
          <label>Firma *</label>
          <input className="audit-form-input" required value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Firma s.r.o." />
        </div>
      </div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>E-mail *</label>
          <input className="audit-form-input" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jan@firma.cz" />
        </div>
        <div className="audit-form-group">
          <label>Telefon</label>
          <input className="audit-form-input" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+420..." />
        </div>
      </div>
      <div className="audit-form-group">
        <label>Co u vás nejvíc pálí? (nepovinné)</label>
        <textarea className="audit-form-textarea" rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Např. ručně importujeme výpisy, PayPal platby se nepárují..." />
      </div>
      <button type="submit" className="audit-form-submit" disabled={submitting}>
        {submitting ? 'Odesílám…' : 'Chci audit zdarma'}
      </button>
      {status && <div className={`audit-form-status ${status.type}`}>{status.msg}</div>}
      <p className="audit-form-fine">Ozveme se do 24 hodin. Obvykle dřív.</p>
    </form>
  );
}

/* ============================================
   FAQ
   ============================================ */

function Faq() {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: 'Je audit opravdu zdarma?', a: 'Ano. 60 minut auditu, PDF plán s konkrétními čísly pro vaši firmu, i 15minutová follow-up konzultace nad výstupem. Nic neplatíte. I když se rozhodnete s námi dál nepokračovat, PDF plán i mobilní aplikace vám zůstávají.' },
    { q: 'Moje firma je specifická — má smysl vůbec volat?', a: 'Každá firma je specifická. Právě to je důvod, proč děláme audity. Na auditu vám poctivě řekneme, co se u vás dá zdigitalizovat a co ne. Pokud něco opravdu nejde, řekneme vám to rovnou.' },
    { q: 'Co od nás na auditu potřebujete?', a: 'Na auditu stačí, když nám popíšete, jaké systémy používáte — e-shop, účetnictví, platební brány, rezervační systém, sklady, výrobu. Nepotřebujeme žádné přístupy ani exporty. To řešíme až po společném rozhodnutí o spolupráci.' },
    { q: 'Máme neobvyklý systém — umíte ho napojit?', a: 'Typicky ano. Napojujeme Pohodu, Money S3, Helios, Flexi, Shoptet, WooCommerce, Shopify, Upgates, GoPay, Comgate, Stripe, PayPal a desítky dalších. Pokud váš systém v nabídce není, doděláme ho na míru. Žádný systém není příliš specifický — každý je nějak datově připojitelný.' },
    { q: 'Jak rychle dostaneme výstup?', a: 'Audit trvá 60 minut. PDF plán vám pošleme do 5 pracovních dnů. Pak si naplánujeme 15minutovou konzultaci, kde projdeme výstup a odpovíme na otázky.' },
    { q: 'Kolik nás to bude stát, když se rozhodneme pokračovat?', a: 'Cena se odvíjí od počtu a složitosti propojení. Na auditu dostanete konkrétní nabídku s čísly. Audit i mobilní aplikaci dostáváte zdarma nezávisle na tom, jestli se pro spolupráci rozhodnete.' },
    { q: 'Proč omezená kapacita?', a: 'Audit děláme osobně, ne přes šablonu. Každé firmě věnujeme reálné 2–3 hodiny přípravy + 60 min hovoru + zpracování PDF. Abychom kvalitu neobětovali kvantitě, pracujeme maximálně s 15 novými klienty měsíčně. Při naplnění otevíráme čekací listinu.' },
  ];
  return (
    <div className="audit-faq">
      {faqs.map((f, i) => (
        <div key={i} className={`audit-faq-item${open === i ? ' open' : ''}`}>
          <button type="button" className="audit-faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
            <span>{f.q}</span>
            <span className="audit-faq-toggle">+</span>
          </button>
          <div className="audit-faq-a">{f.a}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================
   MAIN PAGE
   ============================================ */

export default function AuditEvergreenLanding() {
  useEffect(() => {
    trackPageView('/digitalizacni-audit', 'SyncroX — Digitalizační audit vaší firmy zdarma');
    window.scrollTo(0, 0);

    // Smooth scroll for anchor links
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const a = t.closest('a');
      if (!a || !a.hash || a.origin !== window.location.origin) return;
      const el = document.querySelector(a.hash);
      if (!el) return;
      e.preventDefault();
      const y = (el as HTMLElement).getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="audit-page">
      <Nav />

      {/* ========== HERO ========== */}
      <section className="audit-hero">
        <div className="audit-container">
          <div className="audit-hero-grid">
            <div>
              <div className="rep-hero-trustrow">
                <span className="rep-trust-stars">★★★★★</span>
                <span>4,9 / 5</span>
                <span style={{ color: 'var(--a-muted)' }}>·</span>
                <span>50+ auditů</span>
              </div>
              <div className="audit-hero-eyebrow">
                Digitalizační audit vaší firmy — zdarma · obvyklá hodnota 5 000 Kč
              </div>
              <h1>
                Do 60 minut budete vědět, <span className="audit-underline">kolik vás ročně stojí</span> ruční přepisování dokladů.
              </h1>
              <p className="audit-hero-sub">
                Digitalizační audit vaší firmy. <strong>Odcházíte s PDF plánem v ruce</strong> — s konkrétními čísly pro vaše systémy. I když s námi dál nepokračujete.
              </p>
              <div className="audit-hero-ctas">
                <a href="#form" className="audit-btn-primary">
                  Chci audit zdarma
                  <IconArrow />
                </a>
                <a href="#stack" className="audit-btn-secondary">Co přesně dostanete</a>
              </div>
              <div className="audit-hero-trust">
                <div className="audit-trust-item">
                  <div className="audit-trust-n">60 min</div>
                  <div className="audit-trust-l">Online nebo<br/>u vás</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">PDF plán</div>
                  <div className="audit-trust-l">S konkrétními<br/>čísly pro vaši firmu</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">0 Kč</div>
                  <div className="audit-trust-l">Bez závazku,<br/>bez prodeje</div>
                </div>
              </div>
            </div>
            {/* Hero visual: PDF Mockup */}
            <div className="audit-pdf-mock">
              <div className="audit-pdf-row">
                <div className="audit-pdf-label">Firma na auditu</div>
                <div className="audit-pdf-value audit-pdf-small">E-shop s doplňky stravy · Pohoda · PayPal</div>
              </div>
              <div className="audit-pdf-row">
                <div className="audit-pdf-label">Co lze zdigitalizovat</div>
                <div className="audit-pdf-value audit-pdf-small">
                  Import bankovních výpisů · Párování PayPal plateb · Propojení e-shopu s účetnictvím
                </div>
              </div>
              <div className="audit-pdf-row">
                <div className="audit-pdf-label">Odhad roční úspory</div>
                <div className="audit-pdf-value audit-pdf-big">280 000 Kč</div>
                <div className="audit-pdf-value audit-pdf-small" style={{ marginTop: 6, color: 'var(--a-muted)', fontSize: 13, fontWeight: 500 }}>
                  52 hod/měs přepisování → 3 hod kontroly
                </div>
              </div>
              <div className="audit-pdf-row">
                <div className="audit-pdf-label">Kde začít</div>
                <div className="audit-pdf-value audit-pdf-small">
                  1. Propojit e-shop s Pohodou<br/>
                  2. Napojit PayPal (custom mapování transakčního ID)<br/>
                  3. Manažerský přehled na mobilu
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BIG NUMBER BAND ========== */}
      <section className="audit-bignum">
        <div className="audit-container">
          <div className="audit-bignum-num">280 000 Kč</div>
          <div className="audit-bignum-label">
            <strong>Průměrná roční úspora</strong> klienta po digitalizaci procesů.<br/>
            Zjistěte, co by to znamenalo konkrétně pro vás.
          </div>
        </div>
      </section>

      {/* ========== REFERENCES / CLIENT LOGOS ========== */}
      <section className="audit-refs">
        <div className="audit-container">
          <div className="audit-refs-head">
            <div className="audit-refs-title">Důvěřuje nám</div>
            <div className="audit-refs-sub">Klienti, kterým už data tečou sama</div>
          </div>
          <RefsCarousel />
          <div className="audit-refs-note">
            Mezi klienty e-shopy, výrobní firmy, hotely i služby. <strong>Desítky firem, které přestaly přepisovat data ručně.</strong>
          </div>
        </div>
      </section>

      {/* ========== PROBLEM ========== */}
      <section className="audit-section" id="problem">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Poznáte se?</span>
            <h2>Tohle dnes funguje ve většině českých firem</h2>
            <p>Pokud kývete u 3 a víc bodů, audit je pro vás.</p>
          </div>
          <div className="audit-pains">
            {[
              'Bankovní výpisy a faktury importujete ručně — i když víte, že by to mohlo jít automaticky.',
              'Platby z platebních bran (PayPal, Stripe, GoPay) se nepárují s fakturami a účetní to pak hledá.',
              'Účetní vám pošle manažerský výkaz až po uzávěrce. Mezitím rozhodujete podle pocitu.',
              'Máte 4 a víc systémů (e-shop, fakturace, účetnictví, rezervace, sklady), které spolu nemluví.',
              'Přepisování dat stojí vaši účetní desítky hodin měsíčně — a vy za ten čas platíte.',
              'Bojíte se, že vaše firma je příliš specifická na standardní řešení, a proto to neřešíte.',
              'Už jste to jednou zkoušeli přes programátora a projekt se protáhl na měsíce.',
            ].map((pain, i) => (
              <div key={i} className="audit-pains-item">
                <div className="audit-pains-x">✕</div>
                <div>{pain}</div>
              </div>
            ))}
            <div className="audit-pains-punch">
              Každý den, kdy to neřešíte, vás to <span className="audit-gold-under">tiše stojí peníze</span>,<br/>
              které už nikdy neuvidíte.
            </div>
          </div>
        </div>
      </section>

      {/* ========== BIG TESTIMONIAL ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="rep-quote-block">
            <div className="rep-quote-text">
              Přišli jsme s tím, že <strong>naše firma je moc specifická</strong> — máme výrobu, sklad, tři kanály prodeje. Z auditu jsme odešli s PDF, kde byly přesně vyčíslené úspory pro naši firmu. <strong>180 tisíc ročně</strong>, které nám tiše utíkaly na ručním párování. To mě přesvědčilo.
            </div>
            <div className="rep-quote-author">
              <div className="rep-quote-avatar">MK</div>
              <div className="rep-quote-meta">
                <div className="rep-quote-name">Martin Kovář</div>
                <div className="rep-quote-role">CEO, výrobní firma · 25 zaměstnanců</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== VALUE STACK (Hormozi) ========== */}
      <section className="audit-section audit-section-alt" id="stack">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co v auditu dostanete</span>
            <h2>Kompletní mapa digitalizace<br/>vaší konkrétní firmy</h2>
            <p>Ne obecné brožury. Ne šablony. Vaše systémy, vaše čísla, váš plán.</p>
          </div>
          <div className="audit-stack">
            <div className="audit-stack-header">
              <div>
                <div className="audit-stack-header-l">Hodnota balíčku</div>
                <div className="audit-stack-header-t">Digitalizační audit + bonus</div>
              </div>
            </div>
            <div className="audit-stack-item">
              <div className="audit-stack-l">
                <div className="audit-stack-check"><IconCheck size={14} /></div>
                <div>
                  <div className="audit-stack-title">Analýza řešení vaší firmy</div>
                  <div className="audit-stack-desc">60minutová konzultace s expertem (online nebo u vás) + PDF plán s konkrétními kroky, vyčíslené úspory v Kč a prioritní pořadí. Doručeno do 5 pracovních dnů.</div>
                </div>
              </div>
              <div className="audit-stack-price">5 000 Kč</div>
            </div>
            <div className="audit-stack-item audit-stack-bonus">
              <div className="audit-stack-l">
                <div className="audit-stack-check"><IconCheck size={14} /></div>
                <div>
                  <div className="audit-stack-title">Mobilní aplikace — manažerský přehled</div>
                  <div className="audit-stack-desc">Klíčová čísla vaší firmy v reálném čase. iOS + Android. <strong>Navždy zdarma</strong> — ať už se rozhodnete s námi pokračovat nebo ne.</div>
                </div>
              </div>
              <div className="audit-stack-price">1 500 Kč / měsíc</div>
            </div>
            <div className="audit-stack-total">
              <div className="audit-stack-total-row">
                <div className="audit-stack-total-l">Celková hodnota (analýza + 12 měsíců aplikace)</div>
                <div className="audit-stack-total-v audit-strike">23 000 Kč</div>
              </div>
              <div className="audit-stack-total-row">
                <div className="audit-stack-total-l audit-stack-final-l">Vaše cena</div>
                <div className="audit-stack-total-v audit-stack-final-v">0 Kč</div>
              </div>
            </div>
            <div className="audit-stack-cta-wrap">
              <a href="#form" className="audit-btn-primary">
                Chci audit zdarma
                <IconArrow />
              </a>
              <div style={{ fontSize: 13, color: 'var(--a-muted)', marginTop: 16, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--a-dark)' }}>Transparentně:</strong> Pokud se po auditu rozhodnete spolupráci rozjet, nasazení propojení stojí od <strong style={{ color: 'var(--a-dark)' }}>10 000 Kč</strong> podle složitosti. Cenu dostanete písemně předem.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 3 MINI TESTIMONIALS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co říkají klienti po auditu</span>
            <h2>Bez prodejních řečí.<br/>Jen konkrétní plán.</h2>
          </div>
          <div className="rep-quotes3">
            {[
              {
                stars: 5,
                text: 'Čekal jsem obecnou prezentaci. Dostal jsem <strong>konkrétní čísla — kolik nám ročně utíká na ručním párování</strong>. To mě přesvědčilo.',
                name: 'Jan Novák',
                role: 'Majitel, e-shop s doplňky stravy',
                avatar: 'JN',
              },
              {
                stars: 5,
                text: 'Nejvíc mě oslovilo, že mi <strong>poctivě řekli, co u nás NEjde</strong>. Žádné marketing bullshit. To je důvod, proč jsme nakonec šli dál.',
                name: 'Petra Sýkorová',
                role: 'Jednatelka, rodinný hotel 3*',
                avatar: 'PS',
              },
              {
                stars: 5,
                text: 'Z auditu odcházíme s PDF plánem. <strong>Mobilní aplikaci jsme dostali zdarma.</strong> A nic jsme nemuseli zaplatit. Taky vás to zarazí?',
                name: 'Ondřej Krejčí',
                role: 'Majitel, e-shop + menší výroba',
                avatar: 'OK',
              },
            ].map((q, i) => (
              <div key={i} className="rep-quote-mini">
                <div className="rep-quote-mini-stars">{'★'.repeat(q.stars)}</div>
                <div className="rep-quote-mini-text" dangerouslySetInnerHTML={{ __html: q.text }} />
                <div className="rep-quote-mini-author">
                  <div className="rep-quote-mini-avatar">{q.avatar}</div>
                  <div className="rep-quote-mini-meta">
                    <div className="rep-quote-mini-name">{q.name}</div>
                    <div className="rep-quote-mini-role">{q.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="audit-section" id="how">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Jak to probíhá</span>
            <h2>3 kroky. Žádná složitost.</h2>
            <p>Nemusíte nic připravovat. Stačí napsat.</p>
          </div>
          <div className="audit-steps">
            <div className="audit-step">
              <div className="audit-step-num">KROK 01</div>
              <div className="audit-step-time">2 minuty</div>
              <h3>Napíšete nám</h3>
              <p>Vyplníte krátký formulář nebo pošlete e-mail na <strong>obchod@syncrox.cz</strong>. Stačí jedna věta: „Chci audit." Ozveme se do 24 hodin a domluvíme termín.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 02</div>
              <div className="audit-step-time">60 minut</div>
              <h3>Sejdeme se</h3>
              <p>Online nebo u vás. Projdeme vaše konkrétní systémy, procesy a specifika. Povíme si v lidské řeči, kde se u vás tiše ztrácí čas a peníze.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 03</div>
              <div className="audit-step-time">do 5 dní</div>
              <h3>Dostanete PDF plán</h3>
              <p>Konkrétní plán, vyčíslené úspory, prioritní pořadí. Plus 15minutová konzultace nad výstupem. A mobilní aplikace zdarma — nezávisle na dalším rozhodnutí.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CASE STUDIES ========== */}
      <section className="audit-section audit-section-alt" id="cases">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Čísla od klientů</span>
            <h2>Co digitalizace přinesla<br/>konkrétním firmám</h2>
          </div>
          <div className="audit-cases">
            {[
              {
                tag: 'E-shop s doplňky stravy',
                img: '/cases/case-eshop.jpg',
                alt: 'E-shop sklad s balením objednávek',
                before: '55 hod/měs ručního přepisování',
                after: '3 hod/měs jen kontrola',
                result: '~280 000 Kč úspora ročně',
              },
              {
                tag: 'Rodinný hotel, 3* penzion',
                img: '/cases/case-hotel.jpg',
                alt: 'Rodinný hotel recepce',
                before: 'Rozdíly mezi pokladnou a účtem',
                after: 'Sedí. Sezónu řídí z mobilu.',
                result: 'Nula chyb v účetní uzávěrce',
              },
              {
                tag: 'Výrobní firma, 15 lidí',
                img: '/cases/case-vyroba.jpg',
                alt: 'Výrobní dílna s pracovníky',
                before: '2 plné úvazky na přepisování',
                after: '0,5 úvazku',
                result: '1,5 člověka na produktivní práci',
              },
            ].map((c, i) => (
              <div key={i} className="audit-case">
                <div className="audit-case-img">
                  <img src={c.img} alt={c.alt} loading="lazy" />
                </div>
                <div className="audit-case-head">{c.tag}</div>
                <div className="audit-case-body">
                  <div className="audit-case-row audit-case-row-before">
                    <div className="audit-case-row-label">Dřív</div>
                    <div className="audit-case-row-text">{c.before}</div>
                  </div>
                  <div className="audit-case-row audit-case-row-after">
                    <div className="audit-case-row-label">Dnes</div>
                    <div className="audit-case-row-text">{c.after}</div>
                  </div>
                  <div className="audit-case-result">
                    <div className="audit-case-result-l">Výsledek</div>
                    <div className="audit-case-result-v">{c.result}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STORY (PayPal) ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Reálný příběh ze SyncroX platformy</span>
            <h2>Když klient prodává přes PayPal<br/>a účtuje v Money</h2>
          </div>
          <div className="audit-story">
            <div className="audit-story-label">Case: PayPal ↔ Money S3</div>
            <h3>PayPal nedává platbám variabilní symbol. Dává transakční ID — s písmeny.</h3>
            <p>
              <strong>Problém 1:</strong> Česká banka přijme jen čísla. Písmena se do variabilního symbolu nedostanou.
            </p>
            <p>
              <strong>Problém 2:</strong> Když platba dojde na účet, VS nesedí s fakturou. <strong>Platba visí. Faktura visí.</strong>
            </p>
            <p>
              Na faktuře v Money jsme využili <strong>vedlejší textové pole</strong> — umí písmena i čísla. Celé transakční ID se ukládá sem. Při importu bankovního výpisu se platforma dívá do tohoto pole na faktuře a najde si správnou fakturu sama.
            </p>
            <div className="audit-story-quote">
              A klíčové: tohle si v SyncroX platformě <strong>nastavíte sami</strong>. Bez programátora. Typy dokladů, mapování polí, transformace, pravidla párování — všechno konfigurovatelné.
            </div>
          </div>
        </div>
      </section>

      {/* ========== OBJECTIONS ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co si možná říkáte</span>
            <h2>A proč to je jinak</h2>
          </div>
          <div className="audit-objections">
            {[
              {
                q: 'Moje firma je moc specifická.',
                a: 'Tak si myslí každý. A je to pravda — každá firma je specifická. <strong>Právě proto děláme audity.</strong> Na něm vám poctivě řekneme, co u vás jde a co ne. Když něco nejde, řekneme vám to rovnou.',
              },
              {
                q: 'Už to máme nějak vyřešené.',
                a: 'Fajn. Audit vám ukáže, jestli to máte vyřešené <strong>dobře</strong>. Často najdeme 20–50 % dalších úspor u firem, které „už to mají".',
              },
              {
                q: 'Nemám čas na hodinovou schůzku.',
                a: '60 minut auditu = mapa toho, jak u vás můžete ušetřit 60+ hodin měsíčně. <strong>Matematika souhlasí?</strong>',
              },
              {
                q: 'Určitě je to drahé, když se do toho pustíme.',
                a: 'Audit je zdarma. Pokud potom dává smysl pokračovat, dostanete konkrétní cenovou nabídku <strong>předem</strong>, se všemi čísly. Nic se neskrývá.',
              },
              {
                q: 'Máme neobvyklý systém, co nikdo nezná.',
                a: 'Když ho neumíme napojit ze standardu, <strong>doděláme ho</strong>. Každý systém je nějak datově připojitelný.',
              },
              {
                q: 'Už jsme to zkoušeli a nefungovalo to.',
                a: 'Slyšíme to často. Většinou šlo o programátora, který <strong>neznal účetní logiku</strong>. My děláme obojí — technologii i účtování podle českých standardů.',
              },
            ].map((o, i) => (
              <div key={i} className="audit-obj">
                <div className="audit-obj-q">{o.q}</div>
                <div className="audit-obj-a" dangerouslySetInnerHTML={{ __html: o.a }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== REVIEWS STATS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="rep-reviews">
            <div className="rep-reviews-big">
              <div className="rep-reviews-stars">★★★★★</div>
              <div className="rep-reviews-score">4,9<small>/ 5</small></div>
              <div className="rep-reviews-count">Průměrné hodnocení od 50+ auditů</div>
            </div>
            <div className="rep-reviews-right">
              <h3>97 % klientů říká, že dostali víc, než čekali</h3>
              <p>Nejčastější feedback: <strong>„konečně někdo mluvil lidsky a k věci"</strong>. Žádná PowerPoint prezentace, žádné obecné brožury — jen konkrétní plán pro vaši firmu, v lidské řeči.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== GUARANTEE ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-guarantee">
            <IconShield size={48} />
            <h3>Naše férová nabídka</h3>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', margin: '12px auto 20px', maxWidth: 500, lineHeight: 1.55 }}>
              Žádné podvody, žádné skryté podmínky. Tohle je to, co dostanete — bez ohledu na to, jak se potom rozhodnete.
            </p>
            <div className="audit-guarantee-list">
              {[
                'Audit i PDF plán jsou zdarma. Nic neplatíte.',
                'PDF plán zůstává váš, i když se rozhodnete pokračovat sami.',
                'Mobilní aplikaci dostáváte navždy zdarma — nezávisle na dalším rozhodnutí.',
                'Nezavolá žádný prodejce. Ozve se člověk, který audit vedl.',
                'Všechny ceny za případnou spolupráci jsou transparentní — dostanete je předem, písemně.',
              ].map((item, i) => (
                <div key={i} className="audit-guarantee-item">
                  <IconCheck size={20} />
                  <div>{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== URGENCY ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-urgency">
            <div className="audit-urgency-tag">Omezená kapacita</div>
            <h3>Max 15 nových firem měsíčně</h3>
            <p>Každé firmě věnujeme reálné 2–3 hodiny přípravy + 60 minut hovoru + zpracování PDF plánu. Proto pracujeme maximálně s 15 novými klienty měsíčně. <strong>Čím dřív napíšete, tím rychleji vám odpovíme.</strong></p>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Časté otázky</span>
            <h2>Na co se ptají nejčastěji</h2>
          </div>
          <Faq />
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="audit-cta-section" id="form">
        <div className="audit-container">
          <div className="audit-cta-inner">
            <div className="audit-cta-left">
              <h2>Zjistěte, kolik ročně <span className="audit-gold">zbytečně utrácíte</span> za ruční práci.</h2>
              <p>60 minut. Zdarma. Bez závazku. Bez prodeje. Odejdete s PDF plánem a mobilní aplikací — nezávisle na tom, co se rozhodnete dál.</p>
              <div className="audit-cta-mail">
                <IconMail size={18} />
                obchod@syncrox.cz
              </div>
              <div className="audit-cta-note">Odpovídáme do 24 hodin. Obvykle dřív.</div>

              {/* Mini quote near form */}
              <div style={{
                marginTop: 28,
                padding: '20px 22px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 16,
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 14,
                lineHeight: 1.55,
                maxWidth: 440,
              }}>
                <div style={{ color: '#FDBA74', letterSpacing: '-1px', fontSize: 14, marginBottom: 6 }}>★★★★★</div>
                „<strong style={{ color: '#FFFFFF' }}>Za 60 minut víc hodnoty než z řady 'prezentací' dohromady.</strong> Konkrétní čísla, konkrétní plán, bez tlaku."
                <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>— Tomáš Vlk, jednatel účetní firmy</div>
              </div>
            </div>
            <AuditForm />
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="audit-footer">
        <div className="audit-container">
          <div className="audit-footer-inner">
            <div>© 2026 SyncroX — Platforma pro digitalizaci firem</div>
            <div>
              <a href="/">Hlavní stránka</a>
              <a href="/manazerske-reporty">Manažerské reporty</a>
              <a href="mailto:obchod@syncrox.cz">Kontakt</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
